import { cookiePersistStorage } from "@/helper/common.helper";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { SectionsStoreTypes } from "@/types/user.types";
import {
  IDownloadClickedState,
  IExitPopupState,
  IExpired,
  ILoginQueryParams,
  IPricingExitPopupState,
  IPricingPopupState,
} from "@/types/stateTypes";
import { IANATimezoneDatabaseType, IRegisterTrackType } from "@/types/all.types";

export const useUserState = create(
  persist<SectionsStoreTypes>(
    (set, get) => {
      return {
        version: "1.1.0",
        versionInfo: {},
        guestToken: null,
        queryParams: null,
        hotJarID: null,
        hotJarIDSentStatus: "not-init",
        user: null,
        browserBackClicked: false,
        emailVerifyRequired: false,
        typedEmail: "",
        setVersionInfo: (key, value) => {
          set({ versionInfo: { ...get().versionInfo, [key]: value } });
        },
        setBrowserBackClicked: (browserBackClicked) => {
          set({ browserBackClicked });
        },
        setEmailVerifyRequired: (emailVerifyRequired) => {
          set({ emailVerifyRequired });
        },
        setHotJarIDSentStatus: (value) => {
          set({ hotJarIDSentStatus: value });
        },
        setHotJarID: (hotJarID) => {
          set({ hotJarID });
        },
        setGuestToken: (guestToken) => {
          set({ guestToken });
        },
        setQueryParams: (queryParams) => {
          set({ queryParams });
        },
        setVersion: (version) => {
          set({ version });
        },
        setUser: (user) => {
          if (user) {
            const { tokens, ...userWithoutToken } = user;
            set({ user: userWithoutToken });
          }
        },
        setTypedEmail: (typedEmail) => {
          set({ typedEmail });
        },
      };
    },
    {
      name: "user_state",
      storage: createJSONStorage(() => cookiePersistStorage),
    },
  ),
);

export const useIsExpired = create<IExpired>((set) => ({
  expiredConfig: {
    isExpired: false,
    isReach: false,
    plans: "",
    isDublicate: true,
    isOutOfLimit: {
      status: false,
      currentQrCount: null,
      limit: null,
      name: "",
      uuid: "",
    },
    verifyEmail: false,
  },
  setParams: (val) => {
    set((state) => ({
      expiredConfig: {
        ...state.expiredConfig,
        ...val,
      },
    }));
  },
  setIsOutOfLimit: (val) => {
    set((state) => ({
      expiredConfig: {
        ...state.expiredConfig,
        isOutOfLimit: {
          ...state.expiredConfig.isOutOfLimit,
          ...val,
        },
      },
    }));
  },
  destroyOutOfLimit: () => {
    set((state) => ({
      expiredConfig: {
        ...state.expiredConfig,
        isOutOfLimit: {
          status: false,
          currentQrCount: null,
          limit: null,
          name: "",
          uuid: "",
        },
      },
    }));
  },
}));

export const useRegisterTrack = create(
  persist<IRegisterTrackType>(
    (set) => ({
      trackProperty: null,
      registerInPreview: false,
      setRegisterInPreview: (val) => {
        set({ registerInPreview: false });
      },
      setRegisterTrack: (val) =>
        set((state) => {
          return {
            ...state,
            trackProperty: val,
          };
        }),
    }),
    {
      name: "registerTrack",
      storage: createJSONStorage(() => cookiePersistStorage),
    },
  ),
);

const getCurrentIanaTimezone = (): string => new Intl.DateTimeFormat().resolvedOptions().timeZone;

export const useIANATimezoneDatabase = create(
  persist<IANATimezoneDatabaseType>(
    (set) => ({
      timeZoneDb: getCurrentIanaTimezone(),
      setTimezone: () =>
        set((state) => ({
          ...state,
          timeZoneDb: getCurrentIanaTimezone(),
        })),
    }),
    {
      name: "IANA_Time_Zone_Database",
      storage: createJSONStorage(() => cookiePersistStorage),
    },
  ),
);

export const useExitPopupState = create(
  persist<IExitPopupState>(
    (set) => ({
      hasNotShownPopup: true,
      hidePopup: () => {
        set({
          hasNotShownPopup: false,
        });
      },
    }),
    {
      name: "persistedUserBehaviour",
      storage: createJSONStorage(() => cookiePersistStorage),
    },
  ),
);

export const useDownloadClickedState = create<IDownloadClickedState>((set) => ({
  isDownloadClicked: false,
  setDownloadClicked: (val: boolean) => {
    set({
      isDownloadClicked: val,
    });
  },
}));

export const usePricingPopupState = create(
  persist<IPricingPopupState>(
    (set) => ({
      hasSeenPricingPopup: false,
      hidePricingPopup: () => {
        set({
          hasSeenPricingPopup: true,
        });
      },
    }),
    {
      name: "pricingPopupState",
      storage: createJSONStorage(() => cookiePersistStorage),
    },
  ),
);

export const useLoginQueryParams = create(
  persist<ILoginQueryParams>(
    (set) => ({
      loginParams: {},
      setLoginParams: (val) => {
        set({ loginParams: val });
      },
    }),
    {
      name: "loginQueryParams",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export const usePricingExitPopupState = create(
  persist<IPricingExitPopupState>(
    (set) => ({
      hasSeenExitPopup: false,
      hasVisitedPricing: false,
      hideExitPopup: () => {
        set({ hasSeenExitPopup: true });
      },
      visitPricing: () => {
        set({ hasVisitedPricing: true });
      },
    }),
    {
      name: "pricingExitPopupState",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
